import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import QRCode from 'react-qr-code';
import { useToast } from '../components/shared/ToastNotification';

const INDUSTRIES = [
    { value: 'electronics', label: '📱 Electronics', desc: 'TVs, Phones, Appliances' },
    { value: 'vehicle', label: '🚗 Vehicle', desc: 'Cars, Bikes, Auto Parts' },
    { value: 'grocery', label: '🛒 Grocery', desc: 'Food & Beverages' },
    { value: 'fashion', label: '👔 Fashion', desc: 'Clothing & Accessories' },
    { value: 'shoes', label: '👟 Shoes', desc: 'Footwear & Sneakers' },
    { value: 'construction', label: '🏗️ Construction', desc: 'Hardware & Materials' },
    { value: 'rental', label: '🏠 Rental', desc: 'Equipment & Property' },
    { value: 'dairy', label: '🥛 Milk & Dairy', desc: 'Milk, Eggs & Subscriptions' },
    { value: 'other', label: '📦 Other', desc: 'General Products' }
];

// Premium Store Settings - Restructured
function StoreSettings() {
    const navigate = useNavigate();
    const [firm, setFirm] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploadingLogo, setUploadingLogo] = useState(false);
    const [uploadingBanner, setUploadingBanner] = useState(false);
    const [showSlugWarning, setShowSlugWarning] = useState(false);
    const [showQRCode, setShowQRCode] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');
    const { success, error: showError } = useToast();

    const [formData, setFormData] = useState({
        storefront_enabled: false,
        slug: '',
        slug_locked: false,
        industry: '',
        store_name: '',
        store_description: '',
        store_theme: 'light',
        logo_url: '',
        banner_url: '',
        contact_email: '',
        contact_phone: '',
        whatsapp_number: '',
        google_maps_link: '',
        branch_details: '',
        social_links: {
            instagram: '',
            facebook: '',
            twitter: '',
            linkedin: '',
            youtube: ''
        }
    });

    useEffect(() => {
        loadFirmData();
    }, []);

    const loadFirmData = async () => {
        try {
            setLoading(true);
            const currentFirmId = (window as any).CURRENT_FIRM?.id || (window as any).CURRENT_USER?.firms?.[0]?.id;

            if (currentFirmId) {
                const response = await api.get(`/firms/${currentFirmId}`);
                const firmData = response.firm || response.data?.firm || response.data;

                if (firmData) {
                    setFirm(firmData);
                    setFormData({
                        storefront_enabled: firmData.storefront_enabled || false,
                        slug: firmData.slug || '',
                        slug_locked: firmData.slug_locked || false,
                        industry: firmData.industry || '',
                        store_name: firmData.store_name || firmData.name || '',
                        store_description: firmData.store_description || '',
                        store_theme: firmData.store_theme || 'light',
                        logo_url: firmData.logo_url || '',
                        banner_url: firmData.banner_url || '',
                        contact_email: firmData.contact_email || '',
                        contact_phone: firmData.contact_phone || '',
                        whatsapp_number: firmData.whatsapp_number || '',
                        google_maps_link: firmData.google_maps_link || '',
                        branch_details: firmData.settings?.branch_details || '',
                        social_links: {
                            instagram: firmData.settings?.social_links?.instagram || '',
                            facebook: firmData.settings?.social_links?.facebook || '',
                            twitter: firmData.settings?.social_links?.twitter || '',
                            linkedin: firmData.settings?.social_links?.linkedin || '',
                            youtube: firmData.settings?.social_links?.youtube || ''
                        }
                    });
                }
            }
        } catch (err) {
            console.error('Error loading firm:', err);
            showError('Failed to load store settings');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;

        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Auto-generate slug from store name
        if (name === 'store_name' && !formData.slug_locked) {
            const autoSlug = value
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, '-')
                .replace(/^-+|-+$/g, '');
            setFormData(prev => ({ ...prev, slug: autoSlug }));
        }
    };

    const handleSocialChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            social_links: {
                ...prev.social_links,
                [name]: value
            }
        }));
    };

    const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !firm) return;

        const uploadData = new FormData();
        uploadData.append('logo', file);

        try {
            setUploadingLogo(true);
            const response = await fetch(`/api/v1/firms/${firm.id}/upload_logo`, {
                method: 'POST',
                body: uploadData,
                headers: {
                    'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || ''
                }
            });

            const data = await response.json();
            if (data.success) {
                setFormData(prev => ({ ...prev, logo_url: data.logo_url }));
                success('Logo uploaded successfully!');
            } else {
                showError('Failed to upload logo');
            }
        } catch (err) {
            console.error('Logo upload error:', err);
            showError('Failed to upload logo');
        } finally {
            setUploadingLogo(false);
        }
    };

    const copyStoreURL = () => {
        if (!storeUrl) return;
        navigator.clipboard.writeText(storeUrl).then(() => {
            success('Store URL copied to clipboard!');
        }).catch(() => {
            showError('Failed to copy URL');
        });
    };

    const handleBannerUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !firm) return;

        const uploadData = new FormData();
        uploadData.append('banner', file);

        try {
            setUploadingBanner(true);
            const response = await fetch(`/api/v1/firms/${firm.id}/upload_banner`, {
                method: 'POST',
                body: uploadData,
                headers: {
                    'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content || ''
                }
            });

            const data = await response.json();
            if (data.success) {
                setFormData(prev => ({ ...prev, banner_url: data.banner_url }));
                success('Banner uploaded successfully!');
            } else {
                showError('Failed to upload banner');
            }
        } catch (err) {
            console.error('Banner upload error:', err);
            showError('Failed to upload banner');
        } finally {
            setUploadingBanner(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!firm) return;

        // Show warning if slug is being set for first time
        if (!formData.slug_locked && formData.slug && !firm.slug_locked) {
            setShowSlugWarning(true);
            return;
        }

        await saveSettings();
    };

    const saveSettings = async () => {
        try {
            setSaving(true);
            const payload = {
                ...formData,
                settings: {
                    ...(firm.settings || {}),
                    branch_details: formData.branch_details,
                    social_links: formData.social_links
                }
            };
            const response = await api.put(`/firms/${firm.id}`, { firm: payload });

            if (response.success !== false) {
                await loadFirmData();
                success('Store settings saved successfully!');
            } else {
                throw new Error(response.message || 'Failed to save settings');
            }
        } catch (err: any) {
            console.error('Save failed:', err);
            showError(err.message || 'Failed to save settings');
        } finally {
            setSaving(false);
            setShowSlugWarning(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center">
                <div className="w-12 h-12 border-4 border-indigo-500/30 border-t-indigo-600 rounded-full animate-spin mb-4"></div>
                <div className="text-slate-500 font-black tracking-widest uppercase text-[10px]">Loading Store Settings...</div>
            </div>
        );
    }

    const storeUrl = formData.storefront_enabled && formData.slug
        ? `${window.location.origin}/store/firm/${formData.slug}`
        : null;

    const tabs = [
        { id: 'basic', label: 'Basic Settings', icon: '⚙️' },
        { id: 'branding', label: 'Branding', icon: '🎨' },
        { id: 'contact', label: 'Contact', icon: '📞' },
        { id: 'about', label: 'About & Social', icon: '📖' }
    ];

    return (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* QR Code Modal */}
            {showQRCode && storeUrl && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowQRCode(false)}>
                    <div className="bg-white rounded-2xl p-10 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300" onClick={(e) => e.stopPropagation()}>
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 bg-indigo-50 rounded-2xl flex items-center justify-center mx-auto mb-4 text-3xl">
                                📱
                            </div>
                            <h3 className="text-2xl font-black text-slate-900 mb-2">Store QR Code</h3>
                            <p className="text-slate-500 text-sm">Scan to visit your store</p>
                        </div>
                        <div className="bg-white p-6 rounded-xl border-4 border-slate-100 mb-6">
                            <QRCode value={storeUrl} size={256} className="w-full h-auto" />
                        </div>
                        <div className="text-center text-xs text-slate-400 font-mono break-all mb-6 bg-slate-50 p-3 rounded-lg">
                            {storeUrl}
                        </div>
                        <button
                            onClick={() => setShowQRCode(false)}
                            className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold hover:bg-black transition-all"
                        >
                            Close
                        </button>
                    </div>
                </div>
            )}

            {/* Slug Lock Warning Modal */}
            {showSlugWarning && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-10 max-w-md w-full shadow-2xl animate-in zoom-in-95 duration-300">
                        <div className="text-center mb-8">
                            <div className="w-20 h-20 bg-rose-50 rounded-3xl flex items-center justify-center mx-auto mb-6 text-3xl">
                                🔒
                            </div>
                            <h3 className="text-2xl font-black text-slate-900 mb-3 tracking-tight">Lock In Registry?</h3>
                            <p className="text-slate-500 font-bold opacity-75 leading-relaxed">
                                Proceeding will lock your store slug <span className="text-indigo-600">"{formData.slug}"</span> permanently to ensure SEO integrity.
                            </p>
                        </div>
                        <div className="flex flex-col gap-3">
                            <button
                                onClick={saveSettings}
                                className="w-full py-4 bg-slate-900 text-white rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] shadow-xl shadow-slate-200 hover:bg-black transition-all"
                            >
                                Affirm & Finalize
                            </button>
                            <button
                                onClick={() => setShowSlugWarning(false)}
                                className="w-full py-4 bg-white border border-slate-100 text-slate-400 rounded-2xl text-[10px] font-black uppercase tracking-[0.2em] hover:bg-slate-50 transition-all"
                            >
                                Re-evaluate
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Header Section */}
            <div className="mb-8">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 mb-6">
                    <div>
                        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight flex items-center gap-3">
                            <span className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-2xl">🏪</span>
                            Store Settings
                        </h1>
                        <p className="text-slate-500 mt-2 font-medium">Configure your online storefront</p>
                    </div>

                    {/* Store Status Badge */}
                    <div className={`px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2 ${formData.storefront_enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                        <div className={`w-2 h-2 rounded-full ${formData.storefront_enabled ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`}></div>
                        {formData.storefront_enabled ? 'Store Active' : 'Store Inactive'}
                    </div>
                </div>

                {/* Action Buttons Row */}
                {storeUrl && (
                    <div className="flex flex-wrap gap-3">
                        <button
                            type="button"
                            onClick={copyStoreURL}
                            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-sm hover:bg-slate-50 hover:border-indigo-200 transition-all shadow-sm"
                        >
                            <span>📋</span>
                            Copy URL
                        </button>
                        <button
                            type="button"
                            onClick={() => setShowQRCode(true)}
                            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-sm hover:bg-slate-50 hover:border-indigo-200 transition-all shadow-sm"
                        >
                            <span>📱</span>
                            Show QR Code
                        </button>
                        <a
                            href={storeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl font-bold text-sm hover:bg-slate-50 hover:border-indigo-200 transition-all shadow-sm"
                        >
                            <span>👁️</span>
                            Preview Store
                        </a>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={saving}
                            className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 disabled:opacity-50 ml-auto"
                        >
                            <span>💾</span>
                            {saving ? 'Saving...' : 'Save Changes'}
                        </button>
                    </div>
                )}
            </div>

            {/* Tab Navigation */}
            <div className="border-b border-slate-200 mb-8">
                <div className="flex gap-1 overflow-x-auto">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-6 py-3 font-bold text-sm whitespace-nowrap transition-all border-b-2 ${activeTab === tab.id
                                    ? 'border-indigo-600 text-indigo-600'
                                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                                }`}
                        >
                            <span>{tab.icon}</span>
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Settings Tab */}
                {activeTab === 'basic' && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-5 duration-300">
                        {/* Store Enable/Disable */}
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8">
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                                <div className="flex items-center gap-4">
                                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center text-2xl shadow-inner transition-all ${formData.storefront_enabled ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-400'}`}>
                                        🏪
                                    </div>
                                    <div>
                                        <h3 className="text-xl font-black text-slate-900">Public Storefront</h3>
                                        <p className="text-slate-500 text-sm">Make your products publicly accessible</p>
                                    </div>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        name="storefront_enabled"
                                        checked={formData.storefront_enabled}
                                        onChange={handleChange}
                                        className="sr-only peer"
                                    />
                                    <div className="w-14 h-7 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-indigo-600 shadow-inner"></div>
                                    <span className="ml-3 text-sm font-bold text-slate-600">
                                        {formData.storefront_enabled ? 'Enabled' : 'Disabled'}
                                    </span>
                                </label>
                            </div>
                        </div>

                        {formData.storefront_enabled && (
                            <>
                                {/* Industry Selection */}
                                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                                    <label className="block text-sm font-bold text-slate-700 mb-3">Industry</label>
                                    <select
                                        name="industry"
                                        value={formData.industry}
                                        onChange={handleChange}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                    >
                                        <option value="">Select Industry</option>
                                        {INDUSTRIES.map(ind => (
                                            <option key={ind.value} value={ind.value}>
                                                {ind.label} - {ind.desc}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Store Name & Slug */}
                                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-2">Store Name</label>
                                        <input
                                            type="text"
                                            name="store_name"
                                            value={formData.store_name}
                                            onChange={handleChange}
                                            placeholder="My Awesome Store"
                                            className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-bold text-slate-700 mb-2">Store Slug (URL)</label>
                                        <div className="flex items-center">
                                            <span className="bg-slate-100 px-4 py-3 rounded-l-xl text-sm font-medium text-slate-500 border border-r-0 border-slate-200">
                                                /store/firm/
                                            </span>
                                            <input
                                                type="text"
                                                name="slug"
                                                value={formData.slug}
                                                onChange={handleChange}
                                                disabled={formData.slug_locked}
                                                placeholder="my-store"
                                                className={`flex-1 px-4 py-3 rounded-r-xl text-sm font-medium border border-slate-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all ${formData.slug_locked
                                                        ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                                        : 'bg-slate-50 text-slate-900'
                                                    }`}
                                            />
                                        </div>
                                        {formData.slug_locked && (
                                            <p className="text-xs text-slate-500 mt-2 flex items-center gap-1">
                                                <span>🔒</span> Slug is locked for SEO integrity
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Description */}
                                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                                    <label className="block text-sm font-bold text-slate-700 mb-2">Store Description</label>
                                    <textarea
                                        name="store_description"
                                        value={formData.store_description}
                                        onChange={handleChange}
                                        rows={4}
                                        placeholder="Tell customers about your store..."
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
                                    />
                                </div>

                                {/* Theme */}
                                <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                                    <label className="block text-sm font-bold text-slate-700 mb-3">Theme</label>
                                    <select
                                        name="store_theme"
                                        value={formData.store_theme}
                                        onChange={handleChange}
                                        className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                    >
                                        <option value="light">☀️ Light Mode</option>
                                        <option value="dark">🌙 Dark Mode</option>
                                        <option value="auto">🔄 Auto (System)</option>
                                    </select>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Branding Tab */}
                {activeTab === 'branding' && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-5 duration-300">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Logo Upload */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                                <label className="block text-sm font-bold text-slate-700 mb-4">Store Logo</label>
                                <div className="aspect-square bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden relative group hover:border-indigo-300 transition-all">
                                    {formData.logo_url ? (
                                        <img src={formData.logo_url} className="w-full h-full object-contain p-6" alt="Logo" />
                                    ) : (
                                        <div className="text-center text-slate-400">
                                            <div className="text-4xl mb-2">🖼️</div>
                                            <p className="text-sm font-medium">No logo uploaded</p>
                                        </div>
                                    )}
                                    <label className="absolute inset-0 cursor-pointer flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-all bg-indigo-600/90 text-white">
                                        <div className="text-sm font-bold">
                                            {uploadingLogo ? 'Uploading...' : 'Click to Upload'}
                                        </div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleLogoUpload}
                                            disabled={uploadingLogo}
                                            className="hidden"
                                        />
                                    </label>
                                </div>
                            </div>

                            {/* Banner Upload */}
                            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
                                <label className="block text-sm font-bold text-slate-700 mb-4">Store Banner</label>
                                <div className="aspect-square bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 flex items-center justify-center overflow-hidden relative group hover:border-indigo-300 transition-all">
                                    {formData.banner_url ? (
                                        <img src={formData.banner_url} className="w-full h-full object-cover" alt="Banner" />
                                    ) : (
                                        <div className="text-center text-slate-400">
                                            <div className="text-4xl mb-2">🌄</div>
                                            <p className="text-sm font-medium">No banner uploaded</p>
                                        </div>
                                    )}
                                    <label className="absolute inset-0 cursor-pointer flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-all bg-indigo-600/90 text-white">
                                        <div className="text-sm font-bold">
                                            {uploadingBanner ? 'Uploading...' : 'Click to Upload'}
                                        </div>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleBannerUpload}
                                            disabled={uploadingBanner}
                                            className="hidden"
                                        />
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Contact Tab */}
                {activeTab === 'contact' && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-5 duration-300">
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">📧 Contact Email</label>
                                <input
                                    type="email"
                                    name="contact_email"
                                    value={formData.contact_email}
                                    onChange={handleChange}
                                    placeholder="store@example.com"
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">📞 Contact Phone</label>
                                <input
                                    type="tel"
                                    name="contact_phone"
                                    value={formData.contact_phone}
                                    onChange={handleChange}
                                    placeholder="+1 234 567 8900"
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">💬 WhatsApp Number</label>
                                <input
                                    type="tel"
                                    name="whatsapp_number"
                                    value={formData.whatsapp_number}
                                    onChange={handleChange}
                                    placeholder="+1 234 567 8900"
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-700 mb-2">📍 Google Maps Link</label>
                                <input
                                    type="url"
                                    name="google_maps_link"
                                    value={formData.google_maps_link}
                                    onChange={handleChange}
                                    placeholder="https://maps.google.com/..."
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* About & Social Tab */}
                {activeTab === 'about' && (
                    <div className="space-y-6 animate-in fade-in slide-in-from-right-5 duration-300">
                        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-4">
                            <div>
                                <h3 className="text-lg font-black text-slate-900 mb-4 flex items-center gap-2">
                                    <span>🏢</span>
                                    Branch Details
                                </h3>
                                <textarea
                                    name="branch_details"
                                    value={formData.branch_details}
                                    onChange={handleChange}
                                    rows={4}
                                    placeholder="List your branches and their locations..."
                                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
                                />
                            </div>

                            <hr className="border-slate-100" />

                            <div>
                                <h3 className="text-lg font-black text-slate-900 mb-4 flex items-center gap-2">
                                    <span>🌐</span>
                                    Social Media Links
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Instagram</label>
                                        <div className="flex items-center">
                                            <span className="bg-slate-100 px-3 py-3 rounded-l-xl text-lg border border-r-0 border-slate-200">📸</span>
                                            <input
                                                type="url"
                                                name="instagram"
                                                value={formData.social_links.instagram}
                                                onChange={handleSocialChange}
                                                placeholder="https://instagram.com/your-profile"
                                                className="flex-1 bg-slate-50 border border-slate-200 rounded-r-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 transition-all text-slate-900"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Facebook</label>
                                        <div className="flex items-center">
                                            <span className="bg-slate-100 px-3 py-3 rounded-l-xl text-lg border border-r-0 border-slate-200">🔵</span>
                                            <input
                                                type="url"
                                                name="facebook"
                                                value={formData.social_links.facebook}
                                                onChange={handleSocialChange}
                                                placeholder="https://facebook.com/your-page"
                                                className="flex-1 bg-slate-50 border border-slate-200 rounded-r-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 transition-all text-slate-900"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">Twitter / X</label>
                                        <div className="flex items-center">
                                            <span className="bg-slate-100 px-3 py-3 rounded-l-xl text-lg border border-r-0 border-slate-200">🐦</span>
                                            <input
                                                type="url"
                                                name="twitter"
                                                value={formData.social_links.twitter}
                                                onChange={handleSocialChange}
                                                placeholder="https://twitter.com/your-handle"
                                                className="flex-1 bg-slate-50 border border-slate-200 rounded-r-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 transition-all text-slate-900"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">LinkedIn</label>
                                        <div className="flex items-center">
                                            <span className="bg-slate-100 px-3 py-3 rounded-l-xl text-lg border border-r-0 border-slate-200">💼</span>
                                            <input
                                                type="url"
                                                name="linkedin"
                                                value={formData.social_links.linkedin}
                                                onChange={handleSocialChange}
                                                placeholder="https://linkedin.com/company/your-firm"
                                                className="flex-1 bg-slate-50 border border-slate-200 rounded-r-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 transition-all text-slate-900"
                                            />
                                        </div>
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">YouTube</label>
                                        <div className="flex items-center">
                                            <span className="bg-slate-100 px-3 py-3 rounded-l-xl text-lg border border-r-0 border-slate-200">🎥</span>
                                            <input
                                                type="url"
                                                name="youtube"
                                                value={formData.social_links.youtube}
                                                onChange={handleSocialChange}
                                                placeholder="https://youtube.com/@your-channel"
                                                className="flex-1 bg-slate-50 border border-slate-200 rounded-r-xl px-4 py-3 text-sm font-medium text-slate-900 focus:ring-2 focus:ring-indigo-500 transition-all text-slate-900"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </form>
        </div>
    );
}

export default StoreSettings;
